import React from 'react';
import { Calendar, Clock, CheckCircle } from 'lucide-react';
import { DeploymentRequest } from '../../types';
import { startOfWeek, endOfWeek, startOfMonth, endOfMonth, subWeeks, subMonths } from 'date-fns';
import StatsCard from './StatsCard';

interface DeploymentSummaryProps {
  deployments: DeploymentRequest[];
}

const DeploymentSummary: React.FC<DeploymentSummaryProps> = ({ deployments }) => {
  const now = new Date();
  
  // Current week/month stats
  const currentWeekStart = startOfWeek(now);
  const currentWeekEnd = endOfWeek(now);
  const currentMonthStart = startOfMonth(now);
  const currentMonthEnd = endOfMonth(now);
  
  // Previous week/month stats for comparison
  const prevWeekStart = startOfWeek(subWeeks(now, 1));
  const prevWeekEnd = endOfWeek(subWeeks(now, 1));
  const prevMonthStart = startOfMonth(subMonths(now, 1));
  const prevMonthEnd = endOfMonth(subMonths(now, 1));

  const getDeploymentsInRange = (start: Date, end: Date) => {
    return deployments.filter(d => {
      if (!d.requestedAt) return false;
      const deployDate = new Date(d.requestedAt);
      return deployDate >= start && deployDate <= end;
    });
  };

  const currentWeekDeployments = getDeploymentsInRange(currentWeekStart, currentWeekEnd);
  const prevWeekDeployments = getDeploymentsInRange(prevWeekStart, prevWeekEnd);
  const currentMonthDeployments = getDeploymentsInRange(currentMonthStart, currentMonthEnd);
  const prevMonthDeployments = getDeploymentsInRange(prevMonthStart, prevMonthEnd);

  const calculateTrend = (current: number, previous: number) => {
    if (previous === 0) return { value: 0, isPositive: true };
    const change = ((current - previous) / previous) * 100;
    return { value: Math.abs(change), isPositive: change >= 0 };
  };

  const weeklyTrend = calculateTrend(currentWeekDeployments.length, prevWeekDeployments.length);
  const monthlyTrend = calculateTrend(currentMonthDeployments.length, prevMonthDeployments.length);

  // "Today" in IST, compared as YYYY-MM-DD ('en-CA' formats as ISO).
  const istDay = (value: string | Date) =>
    new Date(value).toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
  const todayIST = istDay(new Date());
  // Actually went live today (deployment.deployedAt — also set by an
  // emergency deploy), not merely filed today.
  const deployedToday = deployments.filter(d => {
    const deployedAt = (d as any).deployment?.deployedAt;
    return deployedAt && (d.status as string) !== 'rolled_back' && istDay(deployedAt) === todayIST;
  });
  const filedToday = deployments.filter(d => d.requestedAt && istDay(d.requestedAt) === todayIST);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      <StatsCard
        title="Deployed Today"
        value={deployedToday.length}
        icon={Calendar}
        color="blue"
        subtitle={`${filedToday.length} request${filedToday.length === 1 ? '' : 's'} filed today`}
      />
      <StatsCard
        title="This Week"
        value={currentWeekDeployments.length}
        icon={Clock}
        color="purple"
        trend={{ value: Number(weeklyTrend.value.toFixed(1)), isPositive: weeklyTrend.isPositive, label: 'vs last week' }}
      />
      <StatsCard
        title="This Month"
        value={currentMonthDeployments.length}
        icon={Calendar}
        color="yellow"
        trend={{ value: Number(monthlyTrend.value.toFixed(1)), isPositive: monthlyTrend.isPositive, label: 'vs last month' }}
      />
      <StatsCard
        title="Success Rate"
        value={`${
          currentMonthDeployments.length > 0
            ? Math.round((currentMonthDeployments.filter(d => d.status === 'deployed').length / currentMonthDeployments.length) * 100)
            : 0
        }%`}
        icon={CheckCircle}
        color="green"
        subtitle="This month"
      />
    </div>
  );
};

export default DeploymentSummary;